import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCart } from './cart.jsx';
import { Doodle, NOANIM } from './Doodles.jsx';
import { reportClientError } from './reportError.js';

const IG_URL = 'https://www.instagram.com/kanel.bakery';

function IgIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}
import Home from './pages/Home.jsx';
import Catalogo from './pages/Catalogo.jsx';
import Producto from './pages/Producto.jsx';
import Checkout from './pages/Checkout.jsx';
import Nosotros from './pages/Nosotros.jsx';
import Contacto from './pages/Contacto.jsx';
import Legal from './pages/Legal.jsx';
import Admin from './pages/Admin.jsx';
import DisenaCookieCake from './pages/DisenaCookieCake.jsx';
import PedidoPersonalizado from './pages/PedidoPersonalizado.jsx';
import SeguimientoPedido from './pages/SeguimientoPedido.jsx';

// Red de seguridad ante cualquier error de render no controlado: evita que TODA
// la app se desmonte dejando solo el fondo. Hay un crash detectado solo en un
// Chrome/Android concreto, causa aún sin confirmar (se descartó que fuera el
// lazy-loading de rutas, que llegó a probarse y revertirse); mientras se acota,
// esta red de seguridad limita el daño a cualquier fallo de render futuro.
class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    reportClientError({ kind: 'RouteErrorBoundary', message: error?.message || String(error), stack: (error?.stack || '') + '\n' + (info?.componentStack || '') });
  }
  componentDidUpdate(prevProps) {
    // limpia el error al cambiar de página, SIN forzar remount via key (eso
    // desmontaba/remontaba todo el árbol de golpe en cada navegación — se
    // probó como posible causa del crash de Chrome del 2026-07-21)
    if (this.state.error && prevProps.pathname !== this.props.pathname) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem', textAlign: 'center', color: 'var(--plum, #4A2A45)' }}>
          <p>Vaya, esta página no ha cargado bien.</p>
          <button className="btn btn--primary" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Header() {
  const { count } = useCart();
  return (
    <header className="hdr">
      <Link to="/" className="brand" aria-label="Kanel Bakery — inicio">
        <img src="/brand/logo.png" alt="Kanel Bakery" className="brand-logo" />
      </Link>
      <nav className="nav">
        <NavLink to="/carta">Carta</NavLink>
        <NavLink to="/nosotros">Nosotros</NavLink>
        <a href="/blog/">Blog</a>
        <NavLink to="/contacto">Contacto</NavLink>
        <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="ig-link" aria-label="Instagram @kanel.bakery">
          <IgIcon size={20} />
        </a>
        <Link to="/seguimiento" className="track-btn">Seguimiento</Link>
        <Link to="/pedido" className="cart-btn">
          Mi pedido
          {NOANIM ? (
            count > 0 && <span className="badge">{count}</span>
          ) : (
          <AnimatePresence>
            {count > 0 && (
              <motion.span
                key={count}
                className="badge"
                initial={{ scale: 0 }}
                animate={{ scale: [1, 1.35, 1] }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.35 }}
              >
                {count}
              </motion.span>
            )}
          </AnimatePresence>
          )}
        </Link>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="ftr">
      <div className="ftr-doodle"><Doodle name="whisk" size={48} color="rgba(255,255,255,.55)" /></div>
      <div className="ftr-grid">
        <div>
          <img src="/brand/logo-cream.png" alt="Kanel Bakery" className="ftr-logo" />
          <p>Repostería creativa por encargo en Madrid. Hecho a mano, con mimo y mucha mantequilla.</p>
          <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="ftr-ig">
            <IgIcon size={18} /> @kanel.bakery
          </a>
        </div>
        <div>
          <h4>La carta</h4>
          <Link to="/carta">Cookie cakes</Link>
          <Link to="/carta">Cheesecakes</Link>
          <Link to="/carta">Tartas personalizadas</Link>
        </div>
        <div>
          <h4>Kanel</h4>
          <Link to="/disena-tu-cookie-cake">Diseña tu cookie cake</Link>
          <Link to="/pedido-personalizado">Pedido personalizado</Link>
          <Link to="/seguimiento">Estado de mi pedido</Link>
          <Link to="/nosotros">Sobre nosotras</Link>
          <a href="/blog/">Blog</a>
          <Link to="/contacto">Contacto</Link>
          <a href={IG_URL} target="_blank" rel="noopener noreferrer">Instagram</a>
          <Link to="/legal">Aviso legal y privacidad</Link>
        </div>
      </div>
      <div className="ftr-base">
        <span>Hecho con harina, mantequilla y</span>
        {NOANIM ? (
          <span style={{ display: 'inline-flex' }}><Doodle name="heart" size={18} color="var(--rose)" /></span>
        ) : (
          <motion.span
            animate={{ scale: [1, 1.18, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'inline-flex' }}
          >
            <Doodle name="heart" size={18} color="var(--rose)" />
          </motion.span>
        )}
        <span>en Madrid · © {new Date().getFullYear()} Kanel Bakery · por WeltasCorp</span>
      </div>
    </footer>
  );
}

function ScrollTop() {
  const { pathname } = useLocation();
  // Con llaves a propósito: en Chrome 150+ scrollTo devuelve una Promise y, si el
  // efecto la devuelve, React la trata como función de limpieza y crashea toda la
  // app en la siguiente navegación (pantalla en blanco; bug cazado el 2026-07-21).
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <>
      <ScrollTop />
      {!isAdmin && <Header />}
      <main>
        <RouteErrorBoundary pathname={location.pathname}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/disena-tu-cookie-cake" element={<DisenaCookieCake />} />
            <Route path="/pedido-personalizado" element={<PedidoPersonalizado />} />
            <Route path="/carta" element={<Catalogo />} />
            <Route path="/producto/:slug" element={<Producto />} />
            <Route path="/pedido" element={<Checkout />} />
            <Route path="/seguimiento" element={<SeguimientoPedido />} />
            <Route path="/nosotros" element={<Nosotros />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </RouteErrorBoundary>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}
